import { track } from '~/utils/funnel'
import { type FunnelWhere, scrubFunnelMessage } from '../../shared/funnel'

/**
 * 📈 Error beacon (v7.39, Javi's D13). The v7.33 outage - Start Run threw
 * "useOnboarding is not defined" for ~9.5 hours - was found by reading
 * code, not by a signal. Every uncaught client error now counts under
 * track('error') with its scrubbed message and where it was caught:
 *   - 'onerror':   window 'error' (uncaught exceptions),
 *   - 'rejection': window 'unhandledrejection',
 *   - 'vue':       component errors (Nuxt's `vue:error` hook, which
 *                  NuxtRoot's onErrorCaptured fires for every one) and
 *                  boot/plugin errors (`app:error`).
 *
 * Listen-only, so nothing that already handles errors changes. NOT via
 * vueApp.config.errorHandler: Nuxt installs its own there while booting
 * and removes it on app:suspense:resolve only if it is still the same
 * function, so wrapping it would keep the boot handler alive forever
 * (every later component error would become the full-screen error page)
 * and replace Vue's console logging of the rest.
 *
 * Throttled: at most MAX_PER_LOAD reports a page load, identical messages
 * once. Ignored as noise: ResizeObserver loop warnings, "Script error."
 * with no details (another origin's script), anything whose file or stack
 * is a browser extension or another origin, and Nuxt's HTTP-status errors
 * (a 404 page is not a crash).
 *
 * `enforce: 'pre'` so it listens before the other plugins run their setup.
 */
const MAX_PER_LOAD = 5
const NOISE = /ResizeObserver loop|^Script error\.?$/i
const EXTENSION = /(?:chrome|moz|safari(?:-web)?|ms-browser)-extension:\/\/|webkit-masked-url:/i

function describe(err: unknown, fallback: string): string {
  if (err instanceof Error) {
    return err.name && err.name !== 'Error' ? `${err.name}: ${err.message}` : err.message
  }
  if (typeof err === 'string') return err
  const message = (err as { message?: unknown } | null)?.message
  if (typeof message === 'string') return message
  return fallback || String(err)
}

export default defineNuxtPlugin({
  name: 'funnel-errors',
  enforce: 'pre',
  setup(nuxtApp) {
    const origin = window.location.origin
    const seen = new Set<string>()
    let sent = 0

    const report = (err: unknown, fallback: string, where: FunnelWhere, file = '') => {
      try {
        if (sent >= MAX_PER_LOAD) return
        const stack = err instanceof Error ? String(err.stack ?? '') : ''
        if (EXTENSION.test(file) || EXTENSION.test(stack)) return
        if (file && !file.startsWith(origin)) return // someone else's script
        const message = scrubFunnelMessage(describe(err, fallback))
        if (!message || NOISE.test(message) || seen.has(message)) return
        seen.add(message)
        sent++
        track('error', { message, where })
      } catch { /* the beacon must never become the error */ }
    }

    window.addEventListener('error', (e: ErrorEvent) => {
      // Resource load failures do not bubble to window; this is script errors only.
      if (!e.error && /^Script error\.?$/i.test(e.message ?? '')) return
      report(e.error ?? e.message, e.message ?? '', 'onerror', e.filename ?? '')
    })
    window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
      report(e.reason, 'Unhandled rejection', 'rejection')
    })
    nuxtApp.hook('vue:error', (err) => {
      report(err, 'Vue error', 'vue')
    })
    nuxtApp.hook('app:error', (err) => {
      if (typeof (err as { statusCode?: unknown } | null)?.statusCode === 'number') return
      report(err, 'App error', 'vue')
    })
  },
})
